/**
 * Top Bar Component
 *
 * @package
 */

import { useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { formatPrice } from '../helpers/priceHelpers';
import { useTotalCost } from '../helpers/useTotalCost';
import VatToggle from './VatToggle';

const TopBar = ( {
	selectedBaseModel,
	selectedProducts,
	vatMode,
	setVatMode,
	workshopHourlyRate = 0,
	taxRate = 0,
	quantities = {},
} ) => {
	const { total: totalCost } = useTotalCost( selectedBaseModel, selectedProducts, vatMode, {
		workshopHourlyRate,
		taxRate,
		quantities,
	} );

	const barRef = useRef( null );

	// Expose the bar height so other sticky elements (gallery, accordion headers, summary sidebar) can offset below it.
	useEffect( () => {
		const bar = barRef.current;
		if ( ! bar ) {
			return;
		}

		const setHeightProperty = () => {
			document.documentElement.style.setProperty( '--at-top-bar-height', `${ bar.offsetHeight }px` );
		};

		setHeightProperty();
		const observer = new window.ResizeObserver( setHeightProperty );
		observer.observe( bar );

		return () => {
			observer.disconnect();
			document.documentElement.style.removeProperty( '--at-top-bar-height' );
		};
	}, [] );

	const vehicleUrl = window.location.pathname.replace( '/configure', '' );

	const scrollToSummary = () => {
		const summaryElement = document.querySelector( '.configurator-summary' );
		if ( summaryElement ) {
			// Check if user prefers reduced motion
			const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
			summaryElement.scrollIntoView( { behavior: prefersReducedMotion ? 'auto' : 'smooth' } );
		}
	};

	return (
		<nav ref={ barRef } className="configurator-top-bar" role="navigation" aria-label={ __( 'Configurator navigation', 'at-vehicle-configurator' ) }>
			<div className="configurator-top-bar-left">
				<a href={ vehicleUrl } className="configurator-back-link">
					<span className="configurator-back-link-arrow">←</span>
					<span className="configurator-back-link-text">
						{ __( 'Back to Vehicle', 'at-vehicle-configurator' ) }
					</span>
				</a>
			</div>
			<div className="configurator-top-bar-right">
				<VatToggle vatMode={ vatMode } onToggle={ setVatMode } />
				{ totalCost > 0 && (
					<div className="configurator-total-cost" aria-live="polite" aria-atomic="true">
						{ __( 'Total', 'at-vehicle-configurator' ) }: { formatPrice( totalCost ) }
					</div>
				) }
				<button
					type="button"
					onClick={ scrollToSummary }
					className="configurator-summary-button"
					aria-label={ __( 'Jump to configuration summary', 'at-vehicle-configurator' ) }
				>
					{ __( 'Summary', 'at-vehicle-configurator' ) }
				</button>
			</div>
		</nav>
	);
};

export default TopBar;
